'use client'

import { useRouter } from 'next/navigation'
import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'

import {
  AccountAddress,
  AccountAddressBook,
} from 'lib/components/AccountAddressBook/AccountAddressBook'
// eslint-disable-next-line max-len
import AccountAddressBookAddModal from 'lib/components/AccountAddressBookAddModal/AccountAddressBookAddModal'
// eslint-disable-next-line max-len
import AccountAddressBookDeleteModal from 'lib/components/AccountAddressBookDeleteModal/AccountAddressBookDeleteModal'
// eslint-disable-next-line max-len
import AccountAddressBookEditModal from 'lib/components/AccountAddressBookEditModal/AccountAddressBookEditModal'
import AccountChangePassword from 'lib/components/AccountChangePassword/AccountChangePassword'
import AccountOrderHistory from 'lib/components/AccountOrderHistory/AccountOrderHistory'
// eslint-disable-next-line max-len
import AccountPaymentInformation, {
  AccountPaymentMethod,
} from 'lib/components/AccountPaymentInformation/AccountPaymentInformation'
// eslint-disable-next-line max-len
import AccountPersonalInformation, {
  AccountType,
  AccountUser,
} from 'lib/components/AccountPersonalInformation/AccountPersonalInformation'
import AccountSmsOptIn from 'lib/components/AccountSmsOptIn/AccountSmsOptIn'
import AccountSmsSignup, { SmsInfo } from 'lib/components/AccountSmsSignup/AccountSmsSignup'
import Header from 'lib/components/Header/Header'
import { useModal } from 'lib/components/Modal/Modal'
import { AccountOrder } from 'lib/components/OrderCard/OrderCard'
import SideNavigation from 'lib/components/SideNavigation/SideNavigation'

import useCreatePaymentMethod from '@/api/useCreatePaymentMethod'
import useCreditCardNonce from '@/api/useCreditCardApi'
import useDeletePaymentMethodApi from '@/api/useDeletePaymentMethodApi'
import usePaymentMethodsApi from '@/api/usePaymentMethodsApi'
import { useSmsMarketingEnabled } from '@/hooks/useSmsMarketingEnabled'
import { redirectBookAgain, redirectFromBookingWidget } from '@/utils/bookingWidgetRedirect'
import { brand } from '@/utils/brand'
import { normalizeStateToCode } from '@/utils/stateCode'

import { useSmsSubscriptionApi } from '../api/useSmsSubscriptionApi'
import { useUser } from '../hooks/useUser'

type ClickEvent = MouseEvent<HTMLAnchorElement>

const scrollToAnchor =
  (anchor: string) =>
  (event: ClickEvent): void => {
    const element = document.querySelector(anchor)
    const header = document.querySelector('header.fixed')

    if (element && header && typeof window !== 'undefined') {
      event.preventDefault()
      window.scrollTo({
        behavior: 'smooth',
        top: element.getBoundingClientRect().top + window.scrollY - header.clientHeight - 24,
      })
    }
  }

const UsersEditPage = (): React.JSX.Element => {
  const [currentAddress, setCurrentAddress] = useState<null | AccountAddress>(null)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [isUserLoaded, setIsUserLoaded] = useState(false)
  const [paymentError, setPaymentError] = useState<Error | null>(null)
  const [shouldShowSms, setShouldShowSms] = useState(true)
  const [showMarketingRow, setShowMarketingRow] = useState(false)
  const [isSmsSubSettled, setIsSmsSubSettled] = useState(false)

  // Runtime per-brand gate for the combined SMS opt-in card (was
  // NEXT_PUBLIC_SMS_MY_ACCOUNT_OPTIN). `loaded` lets the render wait for the
  // brand-config fetch so the legacy AccountSmsSignup card doesn't flash in
  // before we know which card to show.
  const { enabled: isSmsOptInEnabled, loaded: isSmsFlagLoaded } = useSmsMarketingEnabled()

  const router = useRouter()
  const { hideDialog, openDialog } = useModal()

  const { data: nonce, mutateAsync: createNonce, isPending: isCreatingNonce } = useCreditCardNonce()
  const { data: previousPaymentMethods } = usePaymentMethodsApi()

  const { mutateAsync: createPaymentMethod, isPending: isCreatingPaymentMethod } =
    useCreatePaymentMethod()

  const { mutateAsync: deletePaymentMethod, isPending: isDeletingPaymentMethod } =
    useDeletePaymentMethodApi()

  const [previousPaymentMethod] = previousPaymentMethods || []

  const {
    addresses,
    addressesError,
    createAddress,
    currentAddressContact,
    deleteAddress,
    hasMoreOrders,
    isCreatingAddress,
    isDeletingAddress,
    isLoading,
    isLoadingMoreOrders,
    isUpdatingAddress,
    isPro,
    loadMoreOrders,
    orders,
    passwordError,
    user,
    update,
    updateAddress,
    updatePassword,
  } = useUser()

  const {
    data: smsSubscription,
    isError: isSmsSubError,
    isSuccess: isSmsSubSuccess,
  } = useSmsSubscriptionApi(isSmsOptInEnabled)

  // Card (and its nav entry) shows while either program still has an open row.
  const shouldShowSmsCard = isSmsOptInEnabled ? shouldShowSms || showMarketingRow : shouldShowSms

  const accountType = useMemo(() => (isPro ? AccountType.Pro : AccountType.Normal), [isPro])

  const accountUser: AccountUser = useMemo(
    () => ({
      clubName: user?.clubName,
      countryCode: user?.countryCode,
      email: user?.email,
      firstName: user?.firstName,
      lastName: user?.lastName,
      pgaNumber: user?.pgaNumber,
      phoneNumber: user?.phoneNumber,
    }),
    [user],
  )

  const contact = useMemo(
    () => currentAddress && currentAddressContact({ ...currentAddress, favorite: false }),
    [currentAddress, currentAddressContact],
  )

  const navItems = useMemo(
    () => [
      {
        title: {
          href: '#manage-account',
          onClick: scrollToAnchor('#manage-account'),
          title: 'Manage account',
        },
        links: [
          {
            href: '#personal-info',
            onClick: scrollToAnchor('#personal-info'),
            title: 'Update personal information',
          },
          shouldShowSmsCard && {
            href: '#sign-up-sms',
            onClick: scrollToAnchor('#sign-up-sms'),
            title: 'Sign up for SMS text notifications',
          },
          {
            href: '#change-password',
            onClick: scrollToAnchor('#change-password'),
            title: 'Change password',
          },
        ].filter(Boolean) as {
          href: string
          onClick: (event: ClickEvent) => void
          title: string
        }[],
      },
      {
        title: {
          href: '#manage-orders',
          onClick: scrollToAnchor('#manage-orders'),
          title: 'Manage orders',
        },
        links: [
          {
            href: '#view-orders',
            onClick: scrollToAnchor('#view-orders'),
            title: 'View orders',
          },
          {
            href: '#view-address-book',
            onClick: scrollToAnchor('#view-address-book'),
            title: 'View address book',
          },
        ],
      },
      {
        title: {
          href: '#manage-billing',
          onClick: scrollToAnchor('#manage-billing'),
          title: 'Manage billing',
        },
        links: [
          {
            href: '#manage-payment-info',
            onClick: scrollToAnchor('#manage-payment-info'),
            title: 'Manage payment information',
          },
        ],
      },
    ],
    [shouldShowSmsCard],
  )

  const smsInfo: SmsInfo = useMemo(
    () => ({
      smsTrackingOptin: user.smsTrackingOptin,
      smsTrackingPhoneNumber: user.smsTrackingPhoneNumber,
      smsTrackingTermsAccepted: user.smsTrackingTermsAccepted,
    }),
    [user],
  )

  const clickAddAddress = useCallback(
    (event: ClickEvent) => {
      event.preventDefault()
      openDialog('account-address-book-add-modal')
    },
    [openDialog],
  )

  const clickBookAddress = useCallback(
    (kind: 'from' | 'to') => (address: AccountAddress) => (event: ClickEvent) => {
      const addressContact = currentAddressContact({ ...address, favorite: false })
      const base = kind === 'from' ? 'o_' : 'd_'

      const params = {
        ...(address.city && { [`${base}city`]: address.city }),
        ...(addressContact && { [`${base}contact`]: addressContact }),
        ...(address.countryCode && { [`${base}country`]: address.countryCode }),
        ...(address.state && { [`${base}state`]: address.state }),
        ...(address.zip && { [`${base}zip`]: address.zip }),
      }

      const queryString = new URLSearchParams(params).toString()

      event.preventDefault()

      // Hard navigation — a router.push soft-nav into the statically-exported
      // /book/ship can render the raw RSC payload (GSE-11866).
      redirectFromBookingWidget(queryString)
    },
    [currentAddressContact],
  )

  const clickBookOrderAgain = useCallback(({ legs, user }: AccountOrder): void => {
    const [{ shipments, shipRoute }] = legs || [{ shipments: [], shipRoute: null }]

    const calcQty = (productLineName: string): string | undefined => {
      const count = shipments.filter(
        ({ product }) => product?.productLine.name === productLineName,
      ).length

      return count ? count.toString() : undefined
    }

    const product = shipments
      .map(({ product }) => product?.sku)
      .filter(Boolean)
      .join(',')

    // Round trip orders carry an outbound + return leg (both `active`); a single
    // active leg is one way. TAC (cruises-next) reads `trip_type`, the classic
    // /book/ship flow ignores it and reads `oneway`, so send both.
    const activeLegCount = (legs || []).filter((leg) => leg.state === 'active').length
    const isOneWay = activeLegCount <= 1

    const { origin, destination } = shipRoute
    const contactName = `${user.firstName} ${user.lastName}`
    // TAC's StateSelect matches the uppercase 2-letter code strictly; the backend
    // sometimes returns a full name ("Texas") which would blank the dropdown, so
    // normalize before sending (GSE-11919).
    const oState = normalizeStateToCode(origin.state)
    const dState = normalizeStateToCode(destination.state)

    const params = {
      ...(calcQty('Bike') && { bikeqty: calcQty('Bike') }),
      ...(calcQty('Box') && { boxqty: calcQty('Box') }),
      ...(calcQty('Golf') && { golfqty: calcQty('Golf') }),
      ...(calcQty('Luggage') && { luggageqty: calcQty('Luggage') }),
      ...(calcQty('Ski') && { skiqty: calcQty('Ski') }),
      ...(calcQty('Snow Boot') && { snowbootqty: calcQty('Snow Boot') }),
      ...(calcQty('Snowboard') && { snowboardqty: calcQty('Snowboard') }),
      ...(calcQty('Trunk') && { trunkqty: calcQty('Trunk') }),
      // A facility origin/destination carries a facilityId — TAC (cruises-next)
      // resolves it via shipping_from/shipping_to (getFacility) and renders the
      // facility card. Send it so facilities aren't flattened to a plain address
      // (GSE-11919). The street params below remain as a graceful fallback when
      // the facility can't be resolved.
      ...(origin.facilityId && { shipping_from: origin.facilityId }),
      ...(destination.facilityId && { shipping_to: destination.facilityId }),
      // Street address so TAC/booking flow prefill the full line, not just
      // city/state/zip. Both flows map o_address/d_address → origin/destination
      // address line 1.
      ...(destination.address1 && { d_address: destination.address1 }),
      // Business/company name — TAC reads o_company/d_company for the "Company or
      // business name" field. Use companyName (the business), NOT attentionName
      // (the contact person), so business names carry over (GSE-11919).
      ...(destination.companyName && { d_company: destination.companyName }),
      d_city: destination.city,
      d_contact: destination.attentionName || contactName,
      d_country: destination.countryCode,
      ...(dState && { d_state: dState }),
      d_zip: destination.zip5,
      ...(origin.address1 && { o_address: origin.address1 }),
      ...(origin.companyName && { o_company: origin.companyName }),
      o_city: origin.city,
      o_contact: origin.attentionName || contactName,
      o_country: origin.countryCode,
      ...(oState && { o_state: oState }),
      o_zip: origin.zip5,
      product,
      trip_type: isOneWay ? 'oneway' : 'round_trip',
      oneway: isOneWay ? 'true' : 'false',
    }

    const queryString = new URLSearchParams(params).toString()

    // Brand-fixed destination (GSE-11919): ShipSticks re-books via TAC,
    // Ship Skis via the Kettle /book/ship flow.
    redirectBookAgain(queryString)
  }, [])

  const clickDeleteAddress = useCallback(
    (address: AccountAddress) => (event: ClickEvent) => {
      event.preventDefault()
      setCurrentAddress(address)
      openDialog('account-address-book-delete-modal')
    },
    [openDialog],
  )

  const clickViewAddress = useCallback(
    (address: AccountAddress) => (event: ClickEvent) => {
      event.preventDefault()
      setCurrentAddress(address)
      openDialog('account-address-book-edit-modal')
    },
    [openDialog],
  )

  const clickViewOrderDetails = useCallback(
    (id?: string): void => {
      if (id) router.push(`/order-tracking?tracking_id=${id}`)
    },
    [router],
  )

  const createAccountAddress = useCallback(
    (address: Omit<AccountAddress, 'id'>) =>
      createAddress({
        ...Object.fromEntries(
          Object.entries({
            ...address,
            facility: undefined,
            firstName: user.firstName,
            lastName: user.lastName,
          }).map(([k, v]) => [k, v || undefined]),
        ),
        // keep the modal open on failure so the error is visible (GSE-11860)
      }).then((success) => success && hideDialog('account-address-book-add-modal')),
    [createAddress, hideDialog, user.firstName, user.lastName],
  )

  const deleteCurrentAddress = useCallback(
    () =>
      currentAddress &&
      deleteAddress(currentAddress?.id).then(() => {
        hideDialog('account-address-book-edit-modal')
        hideDialog('account-address-book-delete-modal')
      }),
    [currentAddress, deleteAddress, hideDialog],
  )

  const removePaymentMethod = useCallback(
    () => previousPaymentMethod && deletePaymentMethod(previousPaymentMethod.id),
    [deletePaymentMethod, previousPaymentMethod],
  )

  const savePaymentMethod = useCallback(
    (paymentMethod: AccountPaymentMethod) => {
      setIsProcessingPayment(true)

      if (previousPaymentMethod) deletePaymentMethod(previousPaymentMethod.id)

      createNonce({
        number: paymentMethod.number,
        expirationDate: paymentMethod.expirationDate,
        cvv: paymentMethod.cvc,
      }).catch((error) => {
        setPaymentError(error)
        setIsProcessingPayment(false)
      })
    },
    [createNonce, deletePaymentMethod, previousPaymentMethod],
  )

  const updateCurrentAddress = useCallback(
    (address: AccountAddress) =>
      currentAddress &&
      updateAddress({
        ...Object.fromEntries(
          Object.entries({ ...address, facility: undefined }).map(([k, v]) => [k, v || undefined]),
        ),
        id: address?.id || currentAddress.id,
      }).then(() => hideDialog('account-address-book-edit-modal')),
    [currentAddress, hideDialog, updateAddress],
  )

  useEffect(() => {
    if (nonce)
      createPaymentMethod(nonce)
        .then(() => setPaymentError(null))
        .catch(setPaymentError)
        .finally(() => setIsProcessingPayment(false))
  }, [createPaymentMethod, nonce])

  useEffect(() => {
    if (!isUserLoaded && user?.smsTrackingOptin) setShouldShowSms(false)
    if (user?.email) setIsUserLoaded(true)
  }, [isUserLoaded, user])

  useEffect(() => {
    if (!isSmsOptInEnabled || isSmsSubSettled) return
    // Latch once the query settles so refetches or an in-session opt-in can't
    // hide the marketing row mid-visit (mirrors the isUserLoaded latch above).
    if (isSmsSubError) setIsSmsSubSettled(true) // fail closed: row stays hidden
    if (isSmsSubSuccess) {
      setShowMarketingRow(!smsSubscription || smsSubscription.state === 'UNSUBSCRIBED')
      setIsSmsSubSettled(true)
    }
  }, [isSmsOptInEnabled, isSmsSubError, isSmsSubSettled, isSmsSubSuccess, smsSubscription])

  return (
    <>
      <div className="bg-white">
        <div className="mx-auto max-w-[1440px] px-6 py-10 lg:flex lg:gap-8 lg:px-8">
          <SideNavigation title="My Account" items={navItems} />
          <div className="flex flex-col gap-6 lg:flex-1">
            <div id="manage-account">
              <Header as="h2" className="type-headline-3">
                Manage account
              </Header>
            </div>
            <div id="personal-info">
              <AccountPersonalInformation
                isLoading={isLoading}
                onSubmit={update}
                type={accountType}
                user={accountUser}
              />
            </div>
            <div id="sign-up-sms">
              {isSmsFlagLoaded &&
                (isSmsOptInEnabled
                  ? shouldShowSmsCard && (
                      <AccountSmsOptIn
                        brand={brand.key}
                        email={user.email}
                        defaultPhoneNumber={user.smsTrackingPhoneNumber || user.phoneNumber}
                        showTransactionalRow={shouldShowSms}
                        showMarketingRow={showMarketingRow}
                      />
                    )
                  : shouldShowSms && <AccountSmsSignup onSubmit={update} smsInfo={smsInfo} />)}
            </div>
            <div id="change-password">
              <AccountChangePassword error={passwordError} onSubmit={updatePassword} />
            </div>
            <div id="manage-orders">
              <Header as="h2" className="mt-2 type-headline-3 lg:mt-6">
                Manage orders
              </Header>
            </div>
            <div id="view-orders">
              <AccountOrderHistory
                orders={orders}
                viewDetails={clickViewOrderDetails}
                bookAgain={clickBookOrderAgain}
                hasMore={hasMoreOrders}
                isLoadingMore={isLoadingMoreOrders}
                onLoadMore={loadMoreOrders}
              />
            </div>
            <div id="view-address-book">
              <AccountAddressBook
                addresses={addresses}
                onClickAdd={clickAddAddress}
                onClickBookFrom={clickBookAddress('from')}
                onClickBookTo={clickBookAddress('to')}
                onClickDelete={clickDeleteAddress}
                onClickView={clickViewAddress}
              />
            </div>
            <div id="manage-billing">
              <Header as="h2" className="mt-2 type-headline-3 lg:mt-6">
                Manage billing
              </Header>
            </div>
            <div id="manage-payment-info">
              <AccountPaymentInformation
                count={previousPaymentMethods?.length}
                error={paymentError}
                isProcessing={
                  isCreatingNonce ||
                  isCreatingPaymentMethod ||
                  isDeletingPaymentMethod ||
                  isProcessingPayment
                }
                onDelete={removePaymentMethod}
                onSave={savePaymentMethod}
                previousPaymentMethod={previousPaymentMethod}
              />
            </div>
          </div>
        </div>
      </div>
      <AccountAddressBookAddModal
        error={addressesError}
        isCreatingAddress={isCreatingAddress}
        onSubmit={createAccountAddress}
      />
      {currentAddress && (
        <>
          <AccountAddressBookDeleteModal
            contact={contact}
            currentAddress={currentAddress}
            isDeletingAddress={isDeletingAddress}
            onDelete={deleteCurrentAddress}
          />
          <AccountAddressBookEditModal
            contact={contact}
            currentAddress={currentAddress}
            isDeletingAddress={isDeletingAddress}
            isUpdatingAddress={isUpdatingAddress}
            onDelete={deleteCurrentAddress}
            onSave={updateCurrentAddress}
          />
        </>
      )}
    </>
  )
}

export default UsersEditPage
